import React from 'react';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import SurveyItem from '../../util/SurveyItem';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';

export const testIds = {
  confirmStrip: (linkId: string) =>
    getSurveyItemTestId(linkId, 'confirm-strip'),
};

const MISSING = '—';

const FHIR_GENDER_DISPLAY: Record<string, string> = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
  unknown: 'Unknown',
};

const useStyles = makeStyles((theme: Theme) => ({
  strip: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    boxSizing: 'border-box',
  },
  separator: {
    color: theme.palette.text.hint,
    userSelect: 'none',
  },
  name: {
    fontSize: '1rem',
    fontWeight: theme.typography.fontWeightBold,
  },
  prefix: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
  },
  value: {
    fontWeight: 600,
  },
  missing: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
  },
}));

const isBlank = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

const asString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (moment.isMoment(value) && value.isValid()) return value.format();
  return String(value).trim();
};

export const formatDob = (value: unknown): string | null => {
  if (isBlank(value)) return null;
  const parsed = moment(value as moment.MomentInput);
  if (!parsed.isValid()) return null;
  const formatted = parsed.format('MM/DD/YYYY');
  // Age is what staff actually check against the person in the room.
  const age = moment().diff(parsed, 'years');
  return age >= 0 ? `${formatted} (${age}y)` : formatted;
};

export const formatSex = (value: unknown): string | null => {
  const raw = asString(value);
  if (!raw) return null;
  const mapped = FHIR_GENDER_DISPLAY[raw.toLowerCase()];
  if (mapped) return mapped;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

type ConfirmRole = 'name' | 'date' | 'sex' | 'id' | 'other';

const roleForItem = (item: SurveyItem): ConfirmRole => {
  if (item.type === 'date') return 'date';
  const label = (item.label || item.text || '').toLowerCase();
  if (/sex|gender/.test(label)) return 'sex';
  if (/elation|mrn|\bid\b/.test(label)) return 'id';
  if (item.type === 'string') return 'name';
  return 'other';
};

export interface ConfirmSegment {
  key: string;
  kind: 'name' | 'meta';
  prefix?: string;
  value: string;
  missing: boolean;
}

export const buildConfirmSegments = (
  children: SurveyItem[],
  values: Record<string, unknown>,
): ConfirmSegment[] => {
  const nameParts: string[] = [];
  let nameMissing = true;
  const rest: ConfirmSegment[] = [];

  children.filter(Boolean).forEach(child => {
    const value = values[child.link];
    const role = roleForItem(child);
    if (role === 'name') {
      const part = asString(value);
      if (part) {
        nameParts.push(part);
        nameMissing = false;
      }
      return;
    }
    if (role === 'date') {
      const formatted = formatDob(value);
      rest.push({
        key: child.link,
        kind: 'meta',
        prefix: 'DOB',
        value: formatted || MISSING,
        missing: !formatted,
      });
      return;
    }
    if (role === 'sex') {
      const formatted = formatSex(value);
      rest.push({
        key: child.link,
        kind: 'meta',
        // The value is self-identifying; the prefix only earns its place
        // when there is nothing else to identify the segment by.
        prefix: formatted ? undefined : 'Sex',
        value: formatted || MISSING,
        missing: !formatted,
      });
      return;
    }
    if (role === 'id') {
      const id = asString(value);
      rest.push({
        key: child.link,
        kind: 'meta',
        prefix: 'Elation',
        value: id || MISSING,
        missing: !id,
      });
      return;
    }
    const other = asString(value);
    rest.push({
      key: child.link,
      kind: 'meta',
      prefix: child.label || undefined,
      value: other || MISSING,
      missing: !other,
    });
  });

  const nameSegment: ConfirmSegment = {
    key: 'name',
    kind: 'name',
    value: nameParts.length ? nameParts.join(' ') : MISSING,
    missing: nameMissing,
  };

  return [nameSegment, ...rest];
};

export interface ConfirmStripOwnProps {
  surveyItem: SurveyItem;
}

const ConfirmStrip: React.FC<ConfirmStripOwnProps> = ({ surveyItem }) => {
  const classes = useStyles({});
  const { values } = useFormikContext<Record<string, unknown>>();
  const segments = buildConfirmSegments(surveyItem.subItems || [], values);

  return (
    <div
      className={classes.strip}
      role="group"
      aria-label={surveyItem.text || 'Member'}
      {...getTestProps(testIds.confirmStrip(surveyItem.link))}
    >
      {segments.map((segment, index) => (
        <React.Fragment key={segment.key}>
          {index > 0 ? (
            <Text size="body" className={classes.separator} aria-hidden>
              ·
            </Text>
          ) : null}
          <Text
            size="body"
            className={segment.kind === 'name' ? classes.name : undefined}
            aria-label={
              segment.missing
                ? `${segment.prefix || 'Name'} missing`
                : undefined
            }
          >
            {segment.prefix ? (
              <span
                className={segment.missing ? classes.missing : classes.prefix}
              >
                {`${segment.prefix} `}
              </span>
            ) : null}
            <span
              className={
                segment.missing
                  ? classes.missing
                  : segment.kind === 'meta'
                  ? classes.value
                  : undefined
              }
            >
              {segment.value}
            </span>
          </Text>
        </React.Fragment>
      ))}
    </div>
  );
};

export default ConfirmStrip;
